// ShareButton.jsx
import React, { useState, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CopyLinkBox from '../container/CopyLinkBox.js';
import Loading from '../component/Loading.jsx';
import Tooltip from '../component/Tooltip.jsx';

import useShortShareLink, { ShareType } from '../hook/useShortShareLink.js';

import ShareIcon from '../../img/icon-share-fill-shadow.svg';

import CopyIcon from '../../img/ic-copy-link.svg';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';
import { getIsOnMobile } from '../resource/getUserAgent.js';
import copyTextToClipboard from '../resource/copyTextToClipboard.js';
import getCanShare from '../resource/getCanShare.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId } from '../resource/mixpanel.js';
import {
  NULL_FUNCTION,
  EMPTY_OBJECT,
  EMPTY_ARRAY,
} from '../resource/defaults.js';

const DEFAULT_SHARE_TITLE_KEY = 'title_share_flix_link';

export const ShareButton = ({
  id = '',
  tooltipPosition = 'top',
  shareType = null,
  marginRight = 0,
  marginLeft = 0,
  marginBottom = 0,
  isOnMobile = false,
  shareTitleI18nKey = DEFAULT_SHARE_TITLE_KEY,
  shareTitleI18nOption = EMPTY_OBJECT,
  username = '',
  setClickedShareButton = NULL_FUNCTION,
  copyLinkBoxPosition = 'right',
  pushToastr = NULL_FUNCTION,
  customShareIcon = null,
  isAutoHandleShareText = false,
  meId = null,
  userId = null,
  referralCode = '',
  referralSignupRewardDiamond = 0,
  shouldFetchReferral = false,
  fetchMe = NULL_FUNCTION,
  fetchUser = NULL_FUNCTION,
  fetchMeReferral = NULL_FUNCTION,
  templates = EMPTY_ARRAY,
  renderButton = null,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const canShare = getCanShare();

  useEffect(() => {
    if (isAutoHandleShareText && shouldFetchReferral) {
      fetchMeReferral();
    }
  }, [isAutoHandleShareText, shouldFetchReferral, fetchMeReferral]);

  useEffect(() => {
    if (!isAutoHandleShareText) return;

    if (meId && !referralCode) {
      fetchMe();
    }

    if (userId && !username) {
      fetchUser({ userId });
    }
  }, [
    isAutoHandleShareText,
    meId,
    referralCode,
    userId,
    username,
    fetchMe,
    fetchUser,
  ]);

  const getShareTitleKey = () => {
    if (shareTitleI18nKey !== DEFAULT_SHARE_TITLE_KEY) {
      return shareTitleI18nKey;
    }

    if (templates && templates.length > 0) {
      return templates[Math.floor(Math.random() * templates.length)];
    }

    return shareTitleI18nKey;
  };

  const getTranslationOptions = () => {
    if (Object.keys(shareTitleI18nOption).length > 0) {
      return {
        ...shareTitleI18nOption,
        username,
      };
    }

    if (isAutoHandleShareText) {
      return {
        ns: TranslationNamespace.GENERAL,
        username,
        referral_code: referralCode,
        reward_diamond: referralSignupRewardDiamond,
      };
    }

    return {
      username,
    };
  };

  const handleShare = async shortShareLink => {
    setClickedShareButton({ clicked: true });
    if (canShare) {
      const shareText = t(getShareTitleKey(), getTranslationOptions());

      navigator
        .share({
          title: 'SWAG',
          text: shareText,
          url: shortShareLink,
        })
        .catch(() => null);
    } else {
      try {
        await copyTextToClipboard(shortShareLink);
        pushToastr({
          textKey: 'story_copy_link',
          durationSec: 3,
          color: isOnMobile ? 'success' : 'info',
        });
      } catch (_) {
        setIsOpen(true);
      }
    }
  };
  const [isFetchingShortShareLink, onHandleShare] = useShortShareLink({
    targetId: id,
    shareType,
    onShortShareLinkFetched: handleShare,
  });

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const shouldTrackMessageId = !![
    ShareType.SHORT,
    ShareType.STORY,
    ShareType.POST,
  ].includes(shareType);

  const buttonProps = {
    'data-element_id': ButtonId.All.ButtonShare,
    'data-tracking_payload': shouldTrackMessageId && { messageId: id },
    onClick: onHandleShare,
    disabled: isFetchingShortShareLink,
  };

  return (
    <ShareButtonWrapper>
      {typeof renderButton === 'function' ? (
        renderButton({
          buttonProps,
          isFetchingShortShareLink,
          canShare,
        })
      ) : (
        <StyledShareButton
          {...buttonProps}
          marginRight={marginRight}
          marginLeft={marginLeft}
          marginBottom={marginBottom}
          shareType={shareType}
        >
          {isFetchingShortShareLink ? (
            <Loading />
          ) : canShare ? (
            <img
              src={customShareIcon || ShareIcon}
              alt="share"
              width={32}
              height={32}
            />
          ) : (
            <Tooltip
              position={tooltipPosition}
              renderHint={() => (
                <TooltipStyle>
                  {t('copy_link', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </TooltipStyle>
              )}
              trigger="hover"
              disabled={getIsOnMobile()}
            >
              <img src={CopyIcon} alt="copy" width={32} height={32} />
            </Tooltip>
          )}
        </StyledShareButton>
      )}

      {isOpen && (
        <CopyLinkBox
          id={id}
          shareType={shareType}
          handleClose={handleClose}
          position={copyLinkBoxPosition}
        />
      )}
    </ShareButtonWrapper>
  );
};

ShareButton.propTypes = {
  shareType: PropTypes.string,
  username: PropTypes.string,
  id: PropTypes.string,
  tooltipPosition: PropTypes.string,
  marginRight: PropTypes.number,
  marginLeft: PropTypes.number,
  marginBottom: PropTypes.number,
  isOnMobile: PropTypes.bool,
  setClickedShareButton: PropTypes.func,
  pushToastr: PropTypes.func,
  shareTitleI18nKey: PropTypes.string,
  shareTitleI18nOption: PropTypes.object,
  copyLinkBoxPosition: PropTypes.oneOf(['right', 'left', 'center']),
  customShareIcon: PropTypes.string,
  isAutoHandleShareText: PropTypes.bool,
  meId: PropTypes.string,
  userId: PropTypes.string,
  referralCode: PropTypes.string,
  referralSignupRewardDiamond: PropTypes.number,
  shouldFetchReferral: PropTypes.bool,
  fetchMe: PropTypes.func,
  fetchUser: PropTypes.func,
  fetchMeReferral: PropTypes.func,
  templates: PropTypes.array,
  renderButton: PropTypes.func,
};

const ShareButtonWrapper = styled.div`
  position: relative;
`;

const StyledShareButton = styled.button`
  ${ResetButtonStyle}
  margin-right: ${({ marginRight }) => marginRight}px;
  margin-left: ${({ marginLeft }) => marginLeft}px;
  margin-bottom: ${({ marginBottom }) => marginBottom}px;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 4px;

  ${({ shareType }) => {
    if (shareType === ShareType.LIVESTREAM) {
      return css`
        background-color: rgba(25, 25, 25, 0.2);
      `;
    }
    return css`
      @media (hover: hover) {
        :hover {
          background-color: rgba(155, 155, 155, 0.2);
        }
      }
      ${media.mobile`
        width: 32px;
        height: 32px;
        padding: 0;
      `}
    `;
  }}
`;

export const TooltipStyle = styled.div`
  height: 30px;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.5);
  background-color: rgba(246, 246, 246, 0.8);
  font-size: 14px;
  text-align: center;
  white-space: nowrap;
  color: #191919;
  min-width: 72px;
  height: 30px;
  margin-bottom: 4px;
`;

export default ShareButton;
